import React, { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

export default function Resources() {
  const location = useLocation();
  const navigate = useNavigate();

  const scrollToId = (id) => {
    if (!id) return;

    // Try immediately
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      return true;
    }

    // If not found yet (rare), try again shortly after render
    setTimeout(() => {
      const el2 = document.getElementById(id);
      if (el2) el2.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);

    return false;
  };

  useEffect(() => {
    // Preferred: router state (works in HashRouter + BrowserRouter)
    const targetFromState = location.state?.scrollTo;
    if (targetFromState) {
      scrollToId(targetFromState);

      // Clear state so back/forward doesn't keep re-scrolling
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }

    // Optional fallback: if user manually visits /resources#legal on web
    // (This may not work reliably under HashRouter, but it won't break anything)
    const targetFromHash =
      typeof window !== "undefined" ? window.location.hash?.replace("#", "") : "";
    if (targetFromHash) {
      scrollToId(targetFromHash);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-2xl p-8 shadow text-white">
        <h1 className="text-3xl font-bold mb-6 text-center">Resources</h1>

        {/* Quick Links */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4 text-zinc-200">
            Quick Links
          </h2>

          <div className="grid gap-3 sm:grid-cols-2">
            <Link
              to="/resources"
              state={{ scrollTo: "how-it-works" }}
              className="resource-link"
            >
              How SatStacker Works
            </Link>

            <Link to="/faq" className="resource-link">
              Frequently Asked Questions
            </Link>

            <Link
              to="/calculator"
              className="resource-link"
            >
              Sats Calculator
            </Link>

            <Link
              to="/resources"
              state={{ scrollTo: "education" }}
              className="resource-link"
            >
              Educational Articles
            </Link>

            <Link to="/learn" className="resource-link">
              New to Bitcoin?
            </Link>

            <Link
              to="/resources"
              state={{ scrollTo: "security" }}
              className="resource-link"
            >
              Security Best Practices
            </Link>

            <Link
              to="/resources"
              state={{ scrollTo: "legal" }}
              className="resource-link"
            >
              Legal &amp; Compliance
            </Link>
          </div>
        </section>

        {/* How It Works */}
        <section id="how-it-works" className="mb-10">
          <h2 className="text-xl font-semibold mb-3 text-zinc-200">
            How It Works
          </h2>
          <ol className="list-decimal list-inside space-y-2 text-zinc-300">
            <li>Connect your Strike account.</li>
            <li>Choose a Daily, Weekly, or Bi-Weekly schedule.</li>
            <li>SatStacker executes buys automatically.</li>
            <li>Your Bitcoin goes directly to your wallet — no custody.</li>
          </ol>
        </section>

        {/* Education */}
        <section id="education" className="mb-10">
          <h2 className="text-xl font-semibold mb-3 text-zinc-200">
            Educational Articles
          </h2>
          <ul className="space-y-2">
            <li>
              <Link to="/articles/self-custody" className="resource-link">
                Why Self-Custody Matters
              </Link>
            </li>
            <li>
              <Link to="/articles/dca-vs-lump-sum" className="resource-link">
                DCA vs. Lump-Sum: What’s Right for You?
              </Link>
            </li>
          </ul>
        </section>

        {/* Security */}
        <section id="security" className="mb-10">
          <h2 className="text-xl font-semibold mb-3 text-zinc-200">
            Security Best Practices
          </h2>
          <ul className="list-disc list-inside space-y-2 text-zinc-300">
            <li>Use a hardware wallet for long-term storage.</li>
            <li>Enable two-factor authentication where available.</li>
            <li>Keep wallets and devices up to date.</li>
            <li>Verify addresses carefully before sending funds.</li>
          </ul>
        </section>

        {/* Legal */}
        <section id="legal" className="mb-10">
          <h2 className="text-xl font-semibold mb-3 text-zinc-200">
            Legal &amp; Compliance
          </h2>
          <ul className="space-y-2">
            <li>
              <Link to="/terms" className="resource-link">
                Terms of Service
              </Link>
            </li>
            <li>
              <Link to="/privacy" className="resource-link">
                Privacy Policy
              </Link>
            </li>
            <li>
              <Link to="/delete-account-info" className="resource-link">
                How to delete your account
              </Link>
            </li>
          </ul>
        </section>

        {/* Contact */}
        <section id="contact" className="mb-10">
          <h2 className="text-xl font-semibold mb-3 text-zinc-200">
            Contact &amp; Support
          </h2>
          <p className="text-zinc-300">
            Questions? Email{" "}
            <a
              href="mailto:support@satstacker.app"
              className="text-brand hover:underline"
            >
              support@satstacker.app
            </a>
          </p>
        </section>

        {/* Conference Demo — subtle, not in main nav */}
        <section className="pt-6 border-t border-zinc-800">
          <Link
            to="/kiosk"
            className="inline-flex items-center gap-2 text-zinc-600 hover:text-zinc-400 text-sm transition-colors"
          >
            <span>⚡</span>
            <span>Conference Demo</span>
          </Link>
        </section>

      </div>{/* end bg-card */}

      {/* Blog CTA */}
      <div className="text-center">
        <Link
          to="/blog"
          className="inline-block bg-brand text-black px-6 py-2 rounded-xl hover:bg-yellow-300"
        >
          Visit the Blog →
        </Link>
      </div>
    </div>
  );
}